import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { config } from "../config/environment";
import { log, handleApiError } from "../utils/helpers";

interface UserFormProps {
  initialData?: {
    username: string;
    email: string;
    role: "admin" | "staff";
    isActive: boolean;
  };
}

const UserForm: React.FC<UserFormProps> = ({ initialData }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    role: "staff" as "admin" | "staff",
    isActive: true,
    ...initialData,
  });

  useEffect(() => {
    if (id) {
      fetchUser();
    }
  }, [id]);

  const fetchUser = async () => {
    try {
      const response = await fetch(`${config.api.baseUrl}/users/${id}`);
      const data = await response.json();
      setFormData({
        ...data,
        password: "", // Don't show the password
      });
      log("User fetched successfully", "debug", { userId: id });
    } catch (error) {
      const errorMessage = handleApiError(error);
      log("Error fetching user", "error", { error: errorMessage, userId: id });
      console.error("Error fetching user:", error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const url = id
        ? `${config.api.baseUrl}/users/${id}`
        : `${config.api.baseUrl}/users`;
      const method = id ? "PUT" : "POST";

      // Only include password in the request if it's not empty
      const requestData = {
        ...formData,
        ...(formData.password ? {} : { password: undefined }),
      };

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error("Failed to save user");
      }

      navigate("/users");
    } catch (error) {
      console.error("Error saving user:", error);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {id ? "Edit User" : "Add User"}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            required
          />

          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
          />

          <TextField
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            required={!id}
            helperText={id ? "Leave blank to keep current password" : ""}
          />

          <FormControl fullWidth>
            <InputLabel id="role-label">Role</InputLabel>
            <Select
              labelId="role-label"
              name="role"
              value={formData.role}
              label="Role"
              onChange={handleSelectChange}
              required
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="staff">Staff</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={handleSwitchChange}
                name="isActive"
              />
            }
            label="Active"
          />

          <Button type="submit" variant="contained" color="primary" fullWidth>
            {id ? "Update User" : "Create User"}
          </Button>
        </Stack>
      </form>
    </Box>
  );
};

export default UserForm;
